import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import { applyAccountLanguage } from '@/i18n';
import { cacheSet, cacheGet, isOfflineError } from '@/lib/offlineCache';
import { FEATURES, refreshRemoteConfig } from '@/config/features';

import LoginPage from '@/pages/LoginPage';
import SignUp from '@/pages/SignUp';
import ResetPassword from '@/pages/ResetPassword';
import AuthCallback from '@/pages/AuthCallback';
import Onboarding from '@/pages/Onboarding';
import SeasonTemplatePreview from '@/pages/SeasonTemplatePreview';
import { withPremiumGate } from '@/components/withPremiumGate';
import Dashboard from '@/pages/Dashboard';
import GardenPlanner from '@/pages/GardenPlanner';
import BedDesigner from '@/pages/BedDesigner';
import CropLibrary from '@/pages/CropLibrary';
import CropDetail from '@/pages/CropDetail';
import PlantToday from '@/pages/PlantToday';
import PlantingCalendar from '@/pages/PlantingCalendar';
import AICoach from '@/pages/AICoach';
import MyCrops from '@/pages/MyCrops';
import PestScanner from '@/pages/PestScanner';
import SettingsPage from '@/pages/SettingsPage';
import WeatherIntelligence from '@/pages/WeatherIntelligence';
import WateringAdvisor from '@/pages/WateringAdvisor';
import HarvestPredictor from '@/pages/HarvestPredictor';
import SoilAdvisor from '@/pages/SoilAdvisor';
import LearningHub, { LearningModule } from '@/pages/LearningHub';
import Paywall from '@/pages/Paywall';
import YieldAnalytics from '@/pages/YieldAnalytics';
import PestRecords from '@/pages/PestRecords';
import Digest from '@/pages/Digest';
import Today from '@/pages/Today';
import Notifications from '@/pages/Notifications';
import WeeklyPlan from '@/pages/WeeklyPlan';
import Skills from '@/pages/Skills';
import Progress from '@/pages/Progress';
import Community from '@/pages/Community';
import CommunityPost from '@/pages/CommunityPost';
import GardenValue from '@/pages/GardenValue';
import SeedBox from '@/pages/SeedBox';
import SeedScanner from '@/pages/SeedScanner';
import RecommendedVarieties from '@/pages/RecommendedVarieties';
import SupplierDashboard from '@/pages/SupplierDashboard';
import SupplierCommand from '@/pages/SupplierCommand';
import FounderDashboard from '@/pages/FounderDashboard';
import Demo from '@/pages/Demo';
import SeasonPlanner from '@/pages/SeasonPlanner';
import PantryPlanner from '@/pages/PantryPlanner';
import Collective from '@/pages/Collective';
import Shop from '@/pages/Shop';
import Orders from '@/pages/Orders';
import Videos from '@/pages/Videos';
import Messages from '@/pages/Messages';
import Legal from '@/pages/Legal';
import { TourProvider } from '@/welcome/TourContext';
import TourOverlay from '@/components/welcome/TourOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Premium-only tools — free "Taster" growers see a frosted Unlock screen instead.
const PremiumGardenPlanner = withPremiumGate(GardenPlanner, { title: 'Garden Planner', blurb: 'Design and save your garden layout with Homegrown Premium.' });
const PremiumSeasonPlanner = withPremiumGate(SeasonPlanner, { title: 'Season Planner', blurb: 'Plan your whole growing year with Homegrown Premium.' });
const PremiumHarvestPredictor = withPremiumGate(HarvestPredictor, { title: 'Harvest Predictor', blurb: 'See weather-driven harvest dates with Homegrown Premium.' });
const PremiumSoilAdvisor = withPremiumGate(SoilAdvisor, { title: 'Soil Advisor', blurb: 'Unlock tailored soil & feeding guidance with Homegrown Premium.' });
const PremiumGardenValue = withPremiumGate(GardenValue, { title: 'Garden Value', blurb: 'Track the value of everything you grow with Homegrown Premium.' });
const PremiumWateringAdvisor = withPremiumGate(WateringAdvisor, { title: 'Watering Advisor', blurb: 'Get weather-smart watering advice with Homegrown Premium.' });

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/privacy" element={<Legal />} />
      <Route path="/command" element={<SupplierCommand />} />
      <Route path="/founder" element={<FounderDashboard />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/season-template-preview" element={<SeasonTemplatePreview />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute requireOnboarding><Today /></ProtectedRoute>} />
      <Route path="/today" element={<ProtectedRoute requireOnboarding><Today /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute requireOnboarding><Notifications /></ProtectedRoute>} />
      <Route path="/weekly-plan" element={<ProtectedRoute requireOnboarding><WeeklyPlan /></ProtectedRoute>} />
      <Route path="/skills" element={<ProtectedRoute requireOnboarding><Skills /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute requireOnboarding><Progress /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute requireOnboarding><Community /></ProtectedRoute>} />
      <Route path="/community/post/:postId" element={<ProtectedRoute requireOnboarding><CommunityPost /></ProtectedRoute>} />
      <Route path="/collective" element={<ProtectedRoute requireOnboarding><Collective /></ProtectedRoute>} />
      <Route path="/shop" element={FEATURES.shop ? <ProtectedRoute requireOnboarding><Shop /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
      <Route path="/orders" element={<ProtectedRoute requireOnboarding><Orders /></ProtectedRoute>} />
      <Route path="/videos" element={<ProtectedRoute requireOnboarding><Videos /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute requireOnboarding><Messages /></ProtectedRoute>} />
      <Route path="/messages/:userId" element={<ProtectedRoute requireOnboarding><Messages /></ProtectedRoute>} />
      <Route path="/value" element={<ProtectedRoute requireOnboarding><PremiumGardenValue /></ProtectedRoute>} />
      <Route path="/planner" element={<ProtectedRoute requireOnboarding><PremiumGardenPlanner /></ProtectedRoute>} />
      <Route path="/designer/:bedId" element={<ProtectedRoute requireOnboarding><BedDesigner /></ProtectedRoute>} />
      <Route path="/season-plan" element={<ProtectedRoute requireOnboarding><PremiumSeasonPlanner /></ProtectedRoute>} />
      <Route path="/pantry" element={FEATURES.pantry ? <ProtectedRoute requireOnboarding><PantryPlanner /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
      <Route path="/crops" element={<ProtectedRoute requireOnboarding><CropLibrary /></ProtectedRoute>} />
      <Route path="/crops/:cropId" element={<ProtectedRoute requireOnboarding><CropDetail /></ProtectedRoute>} />
      <Route path="/plant-today/:cropId" element={<ProtectedRoute requireOnboarding><PlantToday /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute requireOnboarding><PlantingCalendar /></ProtectedRoute>} />
      <Route path="/coach" element={<ProtectedRoute requireOnboarding><AICoach /></ProtectedRoute>} />
      <Route path="/my-crops" element={<ProtectedRoute requireOnboarding><MyCrops /></ProtectedRoute>} />
      <Route path="/scanner" element={<ProtectedRoute requireOnboarding><PestScanner /></ProtectedRoute>} />
      <Route path="/weather" element={<ProtectedRoute requireOnboarding><WeatherIntelligence /></ProtectedRoute>} />
      <Route path="/watering" element={<ProtectedRoute requireOnboarding><PremiumWateringAdvisor /></ProtectedRoute>} />
      <Route path="/harvests" element={<ProtectedRoute requireOnboarding><PremiumHarvestPredictor /></ProtectedRoute>} />
      <Route path="/soil" element={<ProtectedRoute requireOnboarding><PremiumSoilAdvisor /></ProtectedRoute>} />
      <Route path="/seed-box" element={FEATURES.seedBox ? <ProtectedRoute requireOnboarding><SeedBox /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
      <Route path="/seed-scanner" element={FEATURES.seedBox ? <ProtectedRoute requireOnboarding><SeedScanner /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
      <Route path="/varieties" element={<ProtectedRoute requireOnboarding><RecommendedVarieties /></ProtectedRoute>} />
      <Route path="/supplier-dashboard" element={FEATURES.supplierPortal ? <ProtectedRoute supplierOnly><SupplierDashboard /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
      <Route path="/learn" element={<ProtectedRoute requireOnboarding><LearningHub /></ProtectedRoute>} />
      <Route path="/learn/:moduleId" element={<ProtectedRoute requireOnboarding><LearningModule /></ProtectedRoute>} />
      <Route path="/subscription" element={<Navigate to="/paywall" replace />} />
      <Route path="/paywall" element={<ProtectedRoute requireOnboarding><Paywall /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute requireOnboarding><YieldAnalytics /></ProtectedRoute>} />
      <Route path="/pest-log" element={<ProtectedRoute requireOnboarding><PestRecords /></ProtectedRoute>} />
      <Route path="/digest" element={<ProtectedRoute requireOnboarding><Digest /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute requireOnboarding><SettingsPage /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function ProtectedRoute({ children, requireOnboarding, supplierOnly }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      applyAccountLanguage(location.state.user.language);
      setIsAuthenticated(true);
      
      // Check if supplier trying to access regular routes
      if (location.state.user.role === 'supplier' && !supplierOnly && FEATURES.supplierPortal) {
        navigate('/supplier-dashboard');
        return;
      }
      
      // Check if regular user trying to access supplier routes
      if (supplierOnly && location.state.user.role !== 'supplier') {
        navigate('/dashboard');
        return;
      }
      
      if (requireOnboarding && !location.state.user.onboarded) {
        navigate('/onboarding', { state: { user: location.state.user } });
      }
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        cacheSet('auth_user', response.data);
        applyAccountLanguage(response.data.language);
        setIsAuthenticated(true);
        
        // Check if supplier trying to access regular routes
        if (response.data.role === 'supplier' && !supplierOnly && FEATURES.supplierPortal) {
          navigate('/supplier-dashboard');
          return;
        }
        
        // Check if regular user trying to access supplier routes
        if (supplierOnly && response.data.role !== 'supplier') {
          navigate('/dashboard');
          return;
        }
        
        if (requireOnboarding && !response.data.onboarded) {
          navigate('/onboarding');
        }
      } catch (error) {
        // Offline (network/5xx) with a known session → stay signed in on the
        // last-known user so cached read screens render. A real 401/403 clears it.
        const cached = cacheGet('auth_user');
        if (isOfflineError(error) && cached?.data) {
          setUser(cached.data);
          applyAccountLanguage(cached.data.language);
          setIsAuthenticated(true);
          if (cached.data.role === 'supplier' && !supplierOnly && FEATURES.supplierPortal) { navigate('/supplier-dashboard'); return; }
          if (supplierOnly && cached.data.role !== 'supplier') { navigate('/dashboard'); return; }
          return;
        }
        setIsAuthenticated(false);
        cacheSet('auth_user', null);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state, requireOnboarding, supplierOnly]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E5EBDD]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[#5C665C] font-medium">Loading Homegrown...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? React.cloneElement(children, { user }) : null;
}

function App() {
  useEffect(() => { refreshRemoteConfig(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <TourProvider>
          <AppRouter />
          <TourOverlay />
        </TourProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
